import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/lib/data";

const links = [
  { href: "#services", label: "Services" },
  { href: "#processus", label: "Processus" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2">
          <img
            src={COMPANY.logo}
            alt={COMPANY.name}
            className={`h-10 sm:h-12 w-auto object-contain transition-all ${scrolled ? "" : "brightness-0 invert"}`}
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const fb = e.currentTarget.nextElementSibling;
              if (fb) fb.style.display = "inline";
            }}
          />
          <span
            style={{ display: "none" }}
            className={`font-bold text-lg ${scrolled ? "text-secondary" : "text-white"}`}
          >
            {COMPANY.name}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className={`text-sm font-medium transition-colors ${
                scrolled ? "text-gray-700 hover:text-primary" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="nav-phone"
            className={`flex items-center gap-2 text-sm font-medium ${scrolled ? "text-secondary" : "text-white"}`}
          >
            <Phone className="w-4 h-4" /> {COMPANY.phone}
          </a>
          <Button
            data-testid="nav-cta-book"
            asChild
            className="bg-primary hover:bg-primary/90 rounded-xl"
          >
            <a href="#reservation">Réserver</a>
          </Button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? (
            <X className={scrolled ? "text-secondary" : "text-white"} />
          ) : (
            <Menu className={scrolled ? "text-secondary" : "text-white"} />
          )}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="container-x py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
                className="text-gray-700 font-medium py-2"
              >
                {l.label}
              </a>
            ))}
            <Button asChild data-testid="nav-mobile-cta-book" className="bg-primary rounded-xl mt-2">
              <a href="#reservation" onClick={() => setOpen(false)}>Réserver maintenant</a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
